import {
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";

import { useStore } from "@/store";
import { isDocument } from "@/store/DocumentTreeStore";
import { docPageUrl } from "@/urls";

import { DocItem } from "./DocumentItem";
import { ACCEPTED_IMPORT_FILE_TYPES, FileDndContext } from "./FileDndContext";
import { FileDropOverlay } from "./FileDropOverlay";
import { FolderItem } from "./FolderItem";

import type { FileDragEndEvent } from "./FileDndContext";
import type { FolderFilesItem } from "@/api";
import type { DragOverEvent, DragStartEvent } from "@dnd-kit/core";
import type { ReactNode } from "react";

export type TreeItem = FolderFilesItem & {
  id: string;
  parentId: string;
  depth: number;
  isOpen?: boolean;
  isEditing?: boolean;
  files?: TreeItem[];
  index: number;
  type: "folder" | "document";
};

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

export const DocumentTreeDnD = observer(function DocumentTree({
  children,
  containerRef,
}: {
  children: ReactNode;
  containerRef: React.RefObject<HTMLElement | null>;
}) {
  const router = useRouter();
  const { classroom, documentTree } = useStore();
  const isStudent = classroom.isStudentIn(documentTree.team);
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      distance: 5,
    },
  });
  const pointerSenson = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5,
    },
  });
  const sensors = useSensors(pointerSenson, touchSensor);

  const handleDragStart = ({ active }: DragStartEvent) => {
    documentTree.startDnd(active.id.toString());
    if (active.data.current?.type === "folder" && active.data.current.id) {
      documentTree.closeFolder(active.data.current.id as string);
    }
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    runInAction(() => {
      const id = over?.data.current?.parentId as string | undefined;
      if (id) {
        if (id && documentTree.isOpenFolder(id)) {
          const folder = documentTree.itemById(id);
          if (folder && "files" in folder && folder.files?.length) {
            documentTree.dndHighlightFolderMap.set(folder);
          }
        }
      }
    });
  };

  const handelDragEnd = async ({ active, over, files }: FileDragEndEvent) => {
    // Handle dnd-kit drag and drop
    documentTree.endDnd();

    // Handle file drops
    if (files && files.length > 0) {
      const { parentId, position } = over?.data?.current || {};

      if (typeof parentId === "string" && typeof position === "number" && !isStudent) {
        const firstDocId = await documentTree.enqueueFilesForUpload(files, parentId, position);

        if (firstDocId) {
          router.push(docPageUrl({ docId: firstDocId }));
        }
      }

      return;
    }

    const { parentId, position } = over?.data?.current || {};
    const { parentId: activeParentId, position: activePosition } = active?.data?.current || {};

    if (
      typeof parentId === "string" &&
      typeof position === "number" &&
      typeof activeParentId === "string" &&
      typeof activePosition === "number"
    ) {
      if (isStudent) {
        return;
      }

      const newPosition =
        parentId === activeParentId && position > activePosition ? position - 1 : position;

      if (active.id.toString() !== parentId) {
        documentTree.updateFilePosition(active.id.toString(), parentId, newPosition);
      }
    }
  };

  const handleInvalidFileDrop = (file: File, acceptedTypes: readonly string[]) => {
    console.error("Invalid file type:", file.name, "Accepted:", acceptedTypes);
  };

  const renderActiveItem = () => {
    const activeItem = documentTree.dndActive;
    if (!activeItem) {
      return null;
    }
    return isDocument(activeItem) ? (
      <DocItem docItem={activeItem} isOverlay />
    ) : (
      <FolderItem folder={activeItem} isOverlay />
    );
  };

  return (
    <FileDndContext
      containerRef={containerRef}
      measuring={measuring}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handelDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
      acceptedFileTypes={ACCEPTED_IMPORT_FILE_TYPES}
      onInvalidFileDrop={handleInvalidFileDrop}
    >
      <FileDropOverlay />
      {children}
      {createPortal(<DragOverlay>{renderActiveItem()}</DragOverlay>, document.body)}
    </FileDndContext>
  );
});
